import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiCheckCircle,
  FiXCircle,
  FiActivity,
  FiZap,
  FiDroplet,
  FiEdit2,
  FiSettings
} from "react-icons/fi";
import { IoSpeedometerOutline } from "react-icons/io5";
import { MdSettingsInputAntenna, MdRouter } from "react-icons/md";
import { GiTap } from "react-icons/gi";
import { IotmateButton, DialogBox, StatCard, SectionHeader, InfoCard } from "../../components/Shared";
import { Table } from "../../components/Table";
import fetchDataFromApi from "../../api/IotmateRestApi";
import { generateDummyIrrigationDetails, generateDummyIrrigationHistory } from "../../Data/Data";
import "./ControlUnitIrrigationDetails.css";

const ControlUnitIrrigationDetails = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [irrigationData, setIrrigationData] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newName, setNewName] = useState("");
  // Settings would typically come from API/context - for now using local state for badge display
  const [schedulingMode, setSchedulingMode] = useState("manual");

  useEffect(() => {
    let isInitialLoad = true;

    const fetchData = async () => {
      try {
        if (isInitialLoad) {
          setLoading(true);
        }
        const data = await fetchDataFromApi(`irrigation_details/${deviceId}`);
        setIrrigationData(data || generateDummyIrrigationDetails(deviceId));
      } catch (error) {
        console.error("Error fetching irrigation details:", error);
        setIrrigationData(generateDummyIrrigationDetails(deviceId));
      } finally {
        if (isInitialLoad) {
          setLoading(false);
          isInitialLoad = false;
        }
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [deviceId]);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const data = await fetchDataFromApi(`irrigation_history/${deviceId}`);
        setHistoryData(data || generateDummyIrrigationHistory(deviceId));
      } catch (error) {
        console.error("Error fetching irrigation history:", error);
        setHistoryData(generateDummyIrrigationHistory(deviceId));
      }
    };

    fetchHistory();
  }, [deviceId]);

  const handleEditClick = () => {
    setNewName(irrigationData?.name || "");
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setNewName("");
  };

  const handleSaveName = () => {
    if (newName.trim()) {
      setIrrigationData({ ...irrigationData, name: newName.trim() });
      setIsDialogOpen(false);
      setNewName("");
    }
  };

  const handleSettingsClick = () => {
    navigate(`/irrigation/${deviceId}/settings`);
  };

  const handleSolenoidToggle = (solenoidId) => {
    const updatedSolenoids = irrigationData.solenoids.map(solenoid => {
      if (solenoid.id === solenoidId) {
        return {
          ...solenoid,
          status: solenoid.status === "Open" ? "Closed" : "Open"
        };
      }
      return solenoid;
    });
    setIrrigationData({ ...irrigationData, solenoids: updatedSolenoids });
  };

  const columns = useMemo(
    () => [
      {
        Header: "Time",
        accessor: "timestamp",
        Cell: ({ value }) => (
          <span style={{ color: '#6b7280', fontSize: '0.8125rem' }}>
            {value}
          </span>
        ),
      },
      {
        Header: "Event Type",
        accessor: "eventType",
        Cell: ({ row, value }) => (
          <span style={{ fontWeight: '600', color: '#374151' }}>
            {row.original.icon} {value}
          </span>
        ),
      },
      {
        Header: "Message",
        accessor: "message",
        Cell: ({ value }) => (
          <span style={{ color: '#374151', fontSize: '0.875rem' }}>
            {value}
          </span>
        ),
      },
      {
        Header: "LoRa RX (dBm)",
        accessor: "loraRx",
        Cell: ({ value }) => (
          <span style={{
            color: value > -75 ? '#10b981' : value > -85 ? '#f59e0b' : '#ef4444',
            fontWeight: '600'
          }}>
            {value}
          </span>
        ),
      },
      {
        Header: "LoRa TX (dBm)",
        accessor: "loraTx",
        Cell: ({ value }) => (
          <span style={{
            color: value > -75 ? '#10b981' : value > -85 ? '#f59e0b' : '#ef4444',
            fontWeight: '600'
          }}>
            {value}
          </span>
        ),
      },
    ],
    []
  );

  if (loading) {
    return (
      <div className="irrigation-loading">
        <div className="spinner"></div>
        <p>Loading irrigation details...</p>
      </div>
    );
  }

  if (!irrigationData) {
    return (
      <div className="irrigation-error">
        <p>Failed to load irrigation data</p>
        <button onClick={() => navigate(-1)} className="irrigation-back-btn">
          Back
        </button>
      </div>
    );
  }

  const solenoidsOpen = irrigationData.solenoids.filter(s => s.status === "Open").length;
  const totalFlow = irrigationData.flowMeters.reduce((acc, fm) => acc + fm.totalFlow, 0);
  const pressureNormal = irrigationData.pressureMeters.filter(p => p.status === "Normal").length;

  return (
    <div className="irrigation-container">
      {/* Header */}
      <div className="irrigation-header">
        <button onClick={() => navigate(-1)} className="irrigation-back-btn">
          <FiArrowLeft size={20} />
          Back
        </button>
        <div className="irrigation-title-section">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <h1 className="irrigation-title">
                {irrigationData.name || `Irrigation Unit ${deviceId}`}
              </h1>
              <IotmateButton onClick={handleEditClick} variant="default" className="iotmate-btn-icon">
                <FiEdit2 size={18} />
              </IotmateButton>
            </div>
            <IotmateButton onClick={handleSettingsClick} variant="default" className="iotmate-btn-icon">
              <FiSettings size={18} />
            </IotmateButton>
          </div>
          <div className="irrigation-status">
            {irrigationData.status === "Online" ? (
              <FiCheckCircle size={24} color="#10b981" />
            ) : (
              <FiXCircle size={24} color="#ef4444" />
            )}
            <span
              style={{
                color: irrigationData.status === "Online" ? "#10b981" : "#ef4444",
              }}
            >
              {irrigationData.status || "Offline"}
            </span>
          </div>
        </div>
        <p className="irrigation-last-update">
          Last updated: {irrigationData.lastUpdate || "N/A"}
        </p>
      </div>

      {/* Statistics */}
      <div className="irrigation-stats">
        <StatCard
          value={irrigationData.solenoids.length}
          label="Total Solenoids"
          variant="total"
        />
        <StatCard
          value={solenoidsOpen}
          label="Solenoids Open"
          variant="online"
        />
        <StatCard
          value={irrigationData.flowMeters.length}
          label="Flow Meters"
          variant="connectivity"
        />
        <StatCard
          value={`${pressureNormal}/${irrigationData.pressureMeters.length}`}
          label="Pressure Normal"
          variant="online"
        />
      </div>

      {/* Main Info Cards */}
      <div className="info-card-grid">
        <InfoCard
          icon={<MdRouter />}
          iconColor="#667eea"
          title="Device ID"
          value={irrigationData.deviceId}
        />
        <InfoCard
          icon={<FiActivity />}
          iconColor="#8b5cf6"
          title="Version"
          value={irrigationData.version}
        />
        <InfoCard
          icon={<MdSettingsInputAntenna />}
          iconColor="#10b981"
          title="LoRa Gateway"
          value={irrigationData.loraGateway}
          subtitle={`RX: ${irrigationData.loraRx} dBm | TX: ${irrigationData.loraTx} dBm`}
        />
        <InfoCard
          icon={<FiZap />}
          iconColor="#f59e0b"
          title="Battery"
          value={`${irrigationData.battery}%`}
          subtitle={`Uptime: ${irrigationData.uptime}`}
        />
      </div>

      {/* Flow Meters Section */}
      <div className="irrigation-section">
        <SectionHeader title="Flow Meters" />
        <div className="irrigation-flow-meters-grid">
          {irrigationData.flowMeters.map((flowMeter) => (
            <div key={flowMeter.id} className="flow-meter-card">
              <div className="flow-meter-header">
                <div className="flow-meter-icon">
                  <FiDroplet size={28} color="#3b82f6" />
                </div>
                <div className="flow-meter-info">
                  <h3 className="flow-meter-name">{flowMeter.name}</h3>
                  <span className={`flow-meter-status status-${flowMeter.status.toLowerCase()}`}>
                    {flowMeter.status}
                  </span>
                </div>
              </div>

              <div className="flow-meter-readings">
                <div className="flow-meter-reading">
                  <span className="reading-label">Current Flow</span>
                  <span className="reading-value">{flowMeter.currentFlow} <span className="reading-unit">L/min</span></span>
                </div>
                <div className="flow-meter-reading">
                  <span className="reading-label">Total Flow</span>
                  <span className="reading-value">{flowMeter.totalFlow.toFixed(1)} <span className="reading-unit">L</span></span>
                </div>
                <div className="flow-meter-reading">
                  <span className="reading-label">Today's Flow</span>
                  <span className="reading-value">{flowMeter.todayFlow.toFixed(1)} <span className="reading-unit">L</span></span>
                </div>
              </div>

              <div className="flow-meter-footer">
                <span className="flow-meter-footer-label">Last Reading:</span>
                <span className="flow-meter-footer-value">{flowMeter.lastReading}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pressure Meters Section */}
      <div className="irrigation-section">
        <SectionHeader title="Pressure Meters" />
        <div className="irrigation-pressure-meters-grid">
          {irrigationData.pressureMeters.map((pressureMeter) => (
            <div key={pressureMeter.id} className={`pressure-meter-card ${pressureMeter.status.toLowerCase() === "low" ? "pressure-low" : pressureMeter.status.toLowerCase() === "high" ? "pressure-high" : ""}`}>
              <div className="pressure-meter-header">
                <div className="pressure-meter-icon">
                  <IoSpeedometerOutline size={28} color={pressureMeter.status === "Normal" ? "#10b981" : pressureMeter.status === "Low" ? "#f59e0b" : "#ef4444"} />
                </div>
                <div className="pressure-meter-info">
                  <h3 className="pressure-meter-name">{pressureMeter.name}</h3>
                  <span className="pressure-meter-location">{pressureMeter.location}</span>
                </div>
              </div>

              <div className="pressure-meter-gauge">
                <div className="pressure-value">
                  <span className="pressure-number">{pressureMeter.currentPressure}</span>
                  <span className="pressure-unit">{pressureMeter.unit}</span>
                </div>
                <span className={`pressure-status status-${pressureMeter.status.toLowerCase()}`}>
                  {pressureMeter.status}
                </span>
              </div>

              <div className="pressure-meter-range">
                <div className="pressure-range-item">
                  <span className="range-label">Min</span>
                  <span className="range-value">{pressureMeter.minPressure} {pressureMeter.unit}</span>
                </div>
                <div className="pressure-range-item">
                  <span className="range-label">Max</span>
                  <span className="range-value">{pressureMeter.maxPressure} {pressureMeter.unit}</span>
                </div>
              </div>

              <div className="pressure-meter-footer">
                <span className="pressure-meter-footer-label">Last Reading:</span>
                <span className="pressure-meter-footer-value">{pressureMeter.lastReading}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Total Water Flow Section */}
      <div className="irrigation-section">
        <SectionHeader title="Total Water Consumption" />
        <div className="total-flow-card">
          <div className="total-flow-content">
            <div className="total-flow-item">
              <div className="total-flow-icon">
                <FiDroplet size={40} color="#3b82f6" />
              </div>
              <div className="total-flow-info">
                <span className="total-flow-label">Total Flow (All Meters)</span>
                <span className="total-flow-value">{totalFlow.toFixed(1)} L</span>
              </div>
            </div>
            <div className="total-flow-item">
              <div className="total-flow-icon">
                <FiDroplet size={40} color="#10b981" />
              </div>
              <div className="total-flow-info">
                <span className="total-flow-label">Today's Consumption</span>
                <span className="total-flow-value">
                  {irrigationData.flowMeters.reduce((acc, fm) => acc + fm.todayFlow, 0).toFixed(1)} L
                </span>
              </div>
            </div>
            <div className="total-flow-item">
              <div className="total-flow-icon">
                <FiDroplet size={40} color="#f59e0b" />
              </div>
              <div className="total-flow-info">
                <span className="total-flow-label">Current Flow Rate</span>
                <span className="total-flow-value">
                  {irrigationData.flowMeters.reduce((acc, fm) => acc + fm.currentFlow, 0).toFixed(1)} L/min
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Solenoid Control Section */}
      <div className="irrigation-section">
        <SectionHeader
          title="Solenoid Control Panel"
          rightContent={
            <div className={`schedule-type-badge ${schedulingMode === "time" ? "schedule-time" : "schedule-manual"}`}>
              {schedulingMode === "time" ? "Time-based" : "Manual"}
            </div>
          }
        />
        <div className="irrigation-solenoids-grid">
          {irrigationData.solenoids.map((solenoid) => (
            <div key={solenoid.id} className={`solenoid-card ${solenoid.status === "Open" ? "solenoid-open" : ""}`}>
              <div className="solenoid-header">
                <div className="solenoid-icon">
                  <GiTap size={28} color={solenoid.status === "Open" ? "#10b981" : "#6b7280"} />
                </div>
                <div className="solenoid-info">
                  <h3 className="solenoid-name">{solenoid.name}</h3>
                  <span className="solenoid-zone">{solenoid.zone}</span>
                </div>
              </div>

              <div className="solenoid-status-badge">
                <span className={`solenoid-status ${solenoid.status === "Open" ? "status-open" : "status-closed"}`}>
                  {solenoid.status === "Open" ? "● Open" : "○ Closed"}
                </span>
              </div>

              <div className="solenoid-details">
                <div className="solenoid-detail-item">
                  <span className="solenoid-detail-label">Runtime:</span>
                  <span className="solenoid-detail-value">{solenoid.runtime}</span>
                </div>
                <div className="solenoid-detail-item">
                  <span className="solenoid-detail-label">Cycles:</span>
                  <span className="solenoid-detail-value">{solenoid.cycles}</span>
                </div>
              </div>

              <div className="solenoid-footer">
                <span className="solenoid-footer-text">
                  Last: {solenoid.lastActivated}
                </span>
                <IotmateButton
                  onClick={() => handleSolenoidToggle(solenoid.id)}
                  variant={solenoid.status === "Open" ? "danger" : "primary"}
                  size="small"
                >
                  {solenoid.status === "Open" ? "Close" : "Open"}
                </IotmateButton>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* History Table */}
      <div className="irrigation-history">
        <Table
          columns={columns}
          data={historyData}
          heading="Event History & LoRa Signal Log"
          typeComplex={true}
        />
      </div>

      {/* Edit Name Dialog */}
      <DialogBox
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        title="Edit Irrigation Unit Name"
        onSave={handleSaveName}
        saveLabel="Save"
        cancelLabel="Cancel"
      >
        <input
          type="text"
          className="dialog-input"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Enter irrigation unit name"
          autoFocus
        />
      </DialogBox>
    </div>
  );
};

export default ControlUnitIrrigationDetails;
